import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { entities as entitiesApi } from "../services/api";
import { useApp } from "../context/AppContext";
import { toast } from "../components/Toast";
import {
  BackIcon,
  PlusIcon,
  LocationIcon,
  TrashIcon,
} from "../components/Icons";
import { ConfirmModal } from "../components/Modal";
import DataTable from "../components/DataTable";

function Locations() {
  const navigate = useNavigate();
  const {
    locations,
    locationsLoading,
    locationsHasMore,
    refreshLocations,
    loadMoreLocations,
    deleteEntities,
  } = useApp();
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [stats, setStats] = useState({ totalCount: 0, inContainers: 0 });
  const [searchResults, setSearchResults] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await entitiesApi.stats("location");
      setStats(data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  }, []);

  useEffect(() => {
    refreshLocations({ sort_by: sortField, sort_order: sortDirection });
    loadStats();
  }, [sortField, sortDirection, loadStats]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side search (debounced) so results aren't limited to loaded pages
  useEffect(() => {
    if (!search) {
      setSearchResults(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await entitiesApi.list({
          entity_type: "location",
          search,
          limit: -1,
          sort_by: sortField,
          sort_order: sortDirection,
        });
        if (!cancelled) setSearchResults(data);
      } catch (err) {
        console.error("Failed to search locations:", err);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, sortField, sortDirection]);

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setSelectedIds([]);
  };

  const handleSelectAllAcrossPages = async () => {
    try {
      const { data } = await entitiesApi.list({
        entity_type: "location",
        limit: -1,
        ...(search ? { search } : {}),
      });
      setSelectedIds(data.map((l) => l.id));
    } catch (err) {
      console.error("Failed to select all:", err);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await deleteEntities(selectedIds, "location");
      setSelectedIds([]);
      setShowDeleteConfirm(false);
      loadStats();
    } catch (err) {
      console.error("Failed to delete some items:", err);
      toast.error("Failed to delete some items");
    }
  };

  const formatType = (type) => {
    if (!type) return "-";
    return type.replace(/-/g, " ").replace(/_/g, " ");
  };

  // Server search results while searching (client filter as a fallback
  // until the debounced fetch lands)
  const filteredLocations = search
    ? searchResults ||
      (locations || []).filter((l) =>
        l.name.toLowerCase().includes(search.toLowerCase()),
      )
    : locations || [];

  // Column definitions
  const columns = [
    {
      field: "name",
      header: "Name",
      render: (_, location) => (
        <span
          onClick={() => navigate(`/entity/${location.id}`)}
          style={{
            fontWeight: 500,
            cursor: "pointer",
            color: "var(--accent-primary)",
          }}
        >
          {location.name}
        </span>
      ),
      getSortValue: (item) => (item.name || "").toLowerCase(),
    },
    {
      field: "location_type",
      header: "Type",
      render: (value) => (
        <span style={{ textTransform: "capitalize" }}>{formatType(value)}</span>
      ),
      getSortValue: (item) => (item.location_type || "").toLowerCase(),
    },
    {
      field: "address",
      header: "Address",
      render: (value) => value || "-",
    },
    {
      field: "description",
      header: "Description",
      render: (value) => value || "-",
    },
  ];

  // Card renderer
  const renderCard = (location) => (
    <div
      className="card"
      style={{
        aspectRatio: "1",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "var(--spacing-md)",
        transition: "transform 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "var(--shadow-lg)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "var(--shadow-sm)";
      }}
    >
      <div
        style={{
          marginBottom: "var(--spacing-sm)",
          color: "var(--text-secondary)",
        }}
      >
        <LocationIcon size={40} />
      </div>
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.875rem",
          marginBottom: "var(--spacing-xs)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {location.name}
      </div>
      {location.location_type && (
        <div
          style={{
            color: "var(--text-secondary)",
            fontSize: "0.75rem",
            textTransform: "capitalize",
          }}
        >
          {formatType(location.location_type)}
        </div>
      )}
    </div>
  );

  // Bulk actions
  const bulkActions = [
    {
      label: "Delete",
      icon: TrashIcon,
      onClick: () => setShowDeleteConfirm(true),
      variant: "btn-danger",
    },
  ];

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">Locations</h1>
            <p className="header-subtitle">Track storage locations</p>
          </div>
          <div className="header-actions-right">
            <button
              onClick={() => navigate("/create?type=location")}
              className="btn btn-ghost btn-icon"
            >
              <PlusIcon size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Stats */}
        <div className="stats">
          <div className="stat-card">
            <div className="stat-value">{stats.totalCount}</div>
            <div className="stat-label">Total Locations</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.inContainers}</div>
            <div className="stat-label">Sub-Locations</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {stats.totalCount - stats.inContainers}
            </div>
            <div className="stat-label">Top Level</div>
          </div>
        </div>

        {/* DataTable */}
        <DataTable
          data={filteredLocations}
          loading={locationsLoading}
          columns={columns}
          searchable
          searchPlaceholder="Search locations..."
          searchValue={search}
          onSearchChange={setSearch}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onSelectAllAcrossPages={handleSelectAllAcrossPages}
          totalItemsCount={stats.totalCount}
          viewToggle
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          renderCard={renderCard}
          onRowClick={(location) => navigate(`/entity/${location.id}`)}
          bulkActions={bulkActions}
          hasMore={locationsHasMore && !search}
          onLoadMore={loadMoreLocations}
          emptyIcon={LocationIcon}
          emptyTitle="No locations found"
          emptySubtitle={
            search
              ? "Try a different search term"
              : "Start by adding your first location!"
          }
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleBulkDelete}
        title={`Delete ${selectedIds.length} items?`}
        message={
          <>
            <p>
              Are you sure you want to move {selectedIds.length} location
              {selectedIds.length > 1 ? "s" : ""} to trash?
            </p>
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>
              You can restore them later from the Trash.
            </p>
          </>
        }
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
}

export default Locations;
